import { CSSInterpolation } from '@emotion/serialize';
import type { BaseEmoji } from 'emoji-mart';
import { RefObject, useCallback, useRef, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';

import { useSDK } from '../../../../app';
import type { Theme } from '../../../../theme/interface/detailed';
import { useEmoji } from './hooks/useEmoji';
import { onAttachmentLoad, useOnAttachmentLoad } from './hooks/useOnAttachmentLoad';
import { useOnChange } from './hooks/useOnChange';
import { useOnKeyPress } from './hooks/useOnKeyPress';

export type Interface = {
	value: string;
	onChange: (v: string) => void;
	className: string;
	disabled?: boolean;
	error?: string;
	onSendClick: VoidFunction;
	onSuggestionSelect?: (v: string) => void;
	withAttachments?: boolean;
	showSendIcon?: boolean;
};

export const commonStyles = ({
	theme: {
		allSettings: {
			fields: {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				common: { onFocusBorder, fontSize, border, errorColor, height, ...others },
			},
		},
	},
	error,
}: {
	theme: Theme;
	error?: string;
}): CSSInterpolation => ({
	'& .error-tooltip': { visibility: 'hidden' },
	'&.focus-within': { border: onFocusBorder, '& .error-tooltip': { visibility: 'visible' } },
	'&:focus-within': { border: onFocusBorder, '& .error-tooltip': { visibility: 'initial' } },
	'& textarea': { fontSize },
	display: 'flex',
	'div&': { border: error ? `2px solid ${errorColor}` : border },
	'& .hidden_input': {
		display: 'none',
	},
	position: 'relative',
	...others,
});

export const useMessageField = (initialValue = ''): [string, (v: string) => void, VoidFunction] => {
	const [value, setValue] = useState(initialValue);
	const resetValue = (): void => setValue('');
	return [value, setValue, resetValue];
};

type UseLogicReturnType = {
	onKeyPress: React.KeyboardEventHandler<HTMLTextAreaElement>;
	onAttachmentLoad: onAttachmentLoad | null;
	onChange: (e: string) => void;
	attachmentsInputRef: RefObject<HTMLInputElement>;
	onSendClick: VoidFunction;
	showEmoji: boolean;
	setShowEmoji: (v: boolean) => void;
	onEmojiPick: (v: BaseEmoji) => void;
	textarea: RefObject<HTMLTextAreaElement>;
	scrollbar: RefObject<Scrollbars>;
};

export const useLogic = (
	onSendClickProp: Interface['onSendClick'],
	withAttachments: Interface['withAttachments'],
	onChangeProp: Interface['onChange'],
	value: string
): UseLogicReturnType => {
	const { welcomeDataStore, suggestionsStore, conversationsStore } = useSDK();
	const attachmentsInputRef = useRef<HTMLInputElement>(null);
	const [timer] = useState({ v: 0 });
	const onAttachmentLoad = withAttachments
		? // eslint-disable-next-line react-hooks/rules-of-hooks
		  useOnAttachmentLoad(attachmentsInputRef)
		: null;
	const onSendClick = useCallback(async () => {
		await onSendClickProp();
		await onAttachmentLoad?.onAttachmentLoad();
		timer.v = 0;
	}, [onSendClickProp, timer, onAttachmentLoad]);
	const onKeyPress = useOnKeyPress(onSendClick);
	const onChange = useOnChange(
		suggestionsStore,
		onChangeProp,
		!!welcomeDataStore.data?.showcase.useAutocomplete,
		conversationsStore,
		timer
	);

	const textarea = useRef<HTMLTextAreaElement>(null);
	const scrollbar = useRef<Scrollbars>(null);
	const [showEmoji, setShowEmoji] = useEmoji();

	const onEmojiPick = (v: BaseEmoji): void => {
		const el = textarea.current;
		if (el) {
			const start = el.selectionStart;
			const emoji = value.slice(0, start) + v.native + value.slice(start);
			onChange(emoji);
			/**создание ивента для отправки эмоджи в textarea */
			const emojiEvent = new CustomEvent('addemoji', { detail: emoji });
			el.dispatchEvent(emojiEvent);

			const adjust = start + v.native.length;
			el.focus();
			setTimeout(() => {
				el.setSelectionRange(adjust, adjust);
				if (scrollbar.current) {
					const height = el.style.height;
					const sh = el.scrollHeight;
					el.style.height = sh + 'px';
					if (parseInt(height) < sh && start === value.length) {
						scrollbar.current?.scrollToBottom();
					}
				}
			});
		}
	};
	return {
		onKeyPress,
		onAttachmentLoad,
		onChange,
		attachmentsInputRef,
		onSendClick,
		showEmoji,
		setShowEmoji,
		onEmojiPick,
		textarea,
		scrollbar,
	};
};
